import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HardHat, AlertTriangle, ClipboardList, ShieldCheck, ClipboardCheck, FileText,
  GraduationCap, Siren, Target, Users, Settings as SettingsIcon, ChevronRight,
  Megaphone, ScrollText, Wrench,
} from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import whsService from '../../../services/whs';
import type { DashboardStats } from '../../../types/whs';
import { REPORT_TYPE_LABEL, RISK_LEVEL_LABEL, RISK_LEVEL_CLASS } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

interface Tile {
  path: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
  color: string;
}

function DashboardInner() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.getDashboard();
    if (res.success) setStats(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalOpenReports = stats
    ? stats.reports_by_status.filter((s) => !['closed', 'rejected'].includes(s.status)).reduce((a, s) => a + s.count, 0)
    : 0;
  const totalReports = stats ? stats.reports_by_status.reduce((a, s) => a + s.count, 0) : 0;

  const kpis = [
    { label: 'Open reports', value: totalOpenReports, sub: `${totalReports} total`, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Open actions', value: stats?.actions.open ?? 0, sub: `${stats?.actions.overdue ?? 0} overdue`, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Notifiable pending', value: stats?.notifiable_pending ?? 0, sub: 'awaiting regulator', color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Actions verified', value: stats?.actions.verified ?? 0, sub: 'closed out', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  ];

  const tiles: Tile[] = [
    { path: '/hr/whs/reports', title: 'Incidents & Hazards', desc: 'Reported incidents, near-misses, hazards & illnesses with investigation workflow', icon: <AlertTriangle className="h-6 w-6 text-red-600" />, color: 'bg-red-100' },
    { path: '/hr/whs/actions', title: 'Corrective Actions (CAPA)', desc: 'Track corrective & preventive actions to completion and verification', icon: <ClipboardCheck className="h-6 w-6 text-amber-600" />, color: 'bg-amber-100' },
    { path: '/hr/whs/risk-assessments', title: 'Risk Assessments', desc: 'JSAs, SWMS & risk registers with likelihood × consequence matrix', icon: <ShieldCheck className="h-6 w-6 text-indigo-600" />, color: 'bg-indigo-100' },
    { path: '/hr/whs/inspections', title: 'Inspections', desc: 'Scheduled workplace & plant inspections from checklist templates', icon: <ClipboardList className="h-6 w-6 text-teal-600" />, color: 'bg-teal-100' },
    { path: '/hr/whs/audits', title: 'Audits', desc: 'Internal & external audits with findings and non-conformities', icon: <ScrollText className="h-6 w-6 text-purple-600" />, color: 'bg-purple-100' },
    { path: '/hr/whs/documents', title: 'Safety Documents', desc: 'Policies, SWMS, SOPs, forms and the SDS register (controlled documents)', icon: <FileText className="h-6 w-6 text-slate-600" />, color: 'bg-slate-100' },
    { path: '/hr/whs/training', title: 'Training & Competency', desc: 'Safety inductions, tickets and competencies with expiry tracking', icon: <GraduationCap className="h-6 w-6 text-cyan-600" />, color: 'bg-cyan-100' },
    { path: '/hr/whs/emergency-plans', title: 'Emergency Preparedness', desc: 'Evacuation & emergency plans, wardens and drill scheduling', icon: <Siren className="h-6 w-6 text-orange-600" />, color: 'bg-orange-100' },
    { path: '/hr/whs/objectives', title: 'Objectives & KPIs', desc: 'Safety objectives and targets (LTIFR, TRIFR, action close-out)', icon: <Target className="h-6 w-6 text-emerald-600" />, color: 'bg-emerald-100' },
    { path: '/hr/whs/management-reviews', title: 'Management Reviews', desc: 'ISO 45001 §9.3 management review records (inputs, outputs, decisions)', icon: <Users className="h-6 w-6 text-blue-600" />, color: 'bg-blue-100' },
    { path: '/hr/whs/settings', title: 'WHS Setup & Roles', desc: 'Nominate the WHS manager, HSRs & wardens, policy statement and risk matrix', icon: <SettingsIcon className="h-6 w-6 text-neutral-600" />, color: 'bg-neutral-100' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Workplace Health & Safety"
        subtitle="ISO 45001 aligned safety management — incidents, risk, actions, audits & compliance"
        icon={<HardHat className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {/* Quick worker action */}
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/whs/report')}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium hover:bg-primary-700"
          >
            <Megaphone className="h-4 w-4" /> Report a safety issue
          </button>
          <button
            onClick={() => navigate('/hr/whs/reports?status=submitted')}
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <Wrench className="h-4 w-4" /> Triage new reports
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((k) => (
            <div key={k.label} className={`rounded-xl border border-neutral-200 ${k.bg} p-5`}>
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{k.label}</p>
              <p className={`mt-2 text-3xl font-bold ${k.color}`}>{loading ? '—' : k.value}</p>
              <p className="mt-1 text-xs text-neutral-500">{k.sub}</p>
            </div>
          ))}
        </div>

        {/* Reports by type + risk profile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="rounded-xl border border-neutral-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">Reports by type (last 90 days)</h3>
            {stats && stats.reports_by_type.length > 0 ? (
              <div className="space-y-2">
                {stats.reports_by_type.map((r) => (
                  <div key={r.report_type} className="flex items-center justify-between text-sm">
                    <span className="text-neutral-600">{REPORT_TYPE_LABEL[r.report_type]}</span>
                    <span className="font-semibold text-neutral-900">{r.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-400">No reports in the last 90 days.</p>
            )}
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">Residual risk profile</h3>
            {stats && stats.risk_items_by_level.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {stats.risk_items_by_level.map((r) => (
                  <span key={r.residual_level} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-medium ${RISK_LEVEL_CLASS[r.residual_level]}`}>
                    {RISK_LEVEL_LABEL[r.residual_level]}: {r.count}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-400">No rated risks yet.</p>
            )}
          </div>
        </div>

        {/* Upcoming reviews */}
        {stats && stats.upcoming_reviews.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-8">
            <h3 className="text-sm font-semibold text-amber-900 mb-3">Due for review within 30 days</h3>
            <ul className="space-y-1.5">
              {stats.upcoming_reviews.map((u) => (
                <li key={`${u.kind}-${u.id}`} className="flex items-center justify-between text-sm">
                  <span className="text-amber-900">
                    <span className="text-xs uppercase text-amber-600 mr-2">{u.kind.replace('_', ' ')}</span>
                    {u.title}
                  </span>
                  <span className="text-amber-700">{formatLocalDateInTz(u.due, orgTz)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Section tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((t) => (
            <button
              key={t.path}
              onClick={() => navigate(t.path)}
              className="flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-5 hover:border-primary-300 hover:shadow-sm transition-all text-left"
            >
              <div className="flex items-center gap-4">
                <div className={`${t.color} p-3 rounded-lg shrink-0`}>{t.icon}</div>
                <div>
                  <h3 className="text-base font-semibold text-neutral-900">{t.title}</h3>
                  <p className="text-xs text-neutral-500 mt-0.5">{t.desc}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WhsDashboard() {
  return <WhsGate><DashboardInner /></WhsGate>;
}
